// shared/application/useImagenesEditor.js  (imágenes pendientes de subir + imágenes guardadas). Sin JSX ni Swal.
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { validarImagenes } from './validarImagenes';
import { optimizarImagen } from './optimizarImagen';
import { archivoRepository } from '../infrastructure/archivo';
import { compresorImagen } from '../infrastructure/compresorImagen';
import { conMarcaEliminacion, conProgresoEliminacion, recortarAlLimite, uuidsConMarca, MAX_IMAGENES } from '../domain/media';

/**
 * Estado único { pending, saved } (no hay batching automático tras await en React 17).
 * `saved` se inicializa con el media[] de la entidad: los formularios montan el editor solo cuando la entidad ya
 * cargó (la página renderiza el body con `data` no nulo y remonta si recarga), por eso basta el valor inicial.
 * Cada imagen guardada puede llevar `eliminacion: { estado: 'marked' | 'deleting' | 'error', error }`: está marcada para
 * eliminarse AL GUARDAR (no se llama al API aquí; la ejecuta guardarConHijos). Se puede deshacer hasta entonces.
 * Vive en este hook (no en uno aparte) para que `total` (pendientes + guardadas no marcadas) sea coherente en un solo lugar.
 * Los object URLs de vista previa NO viven aquí: los crea/revoca el componente de UI (API del navegador).
 */
export const useImagenesEditor = ({ saved = [], pendientes = [], archivo = archivoRepository, compresor = compresorImagen } = {}) => {
  // `pendientes` iniciales: [{ uuid, file, estado?, error? }] que sobreviven a un cambio de ruta (crear -> editar).
  const [state, setState] = useState(() => ({
    pending: pendientes.map(p => ({ estado: 'queued', error: null, media: null, ...p })),
    saved,
  }));
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Total vigente (guardadas no marcadas + todas las pendientes) en un ref: addFiles es async y no debe usar un valor viejo.
  const totalRef = useRef(0);

  // Valida por contenido (async), recorta al límite de MAX_IMAGENES, agrega las aceptadas de inmediato como 'procesando'
  // y las comprime UNA A UNA (secuencial); al terminar cada una pasa a 'queued' con el archivo optimizado, o se quita si falla.
  // Devuelve { rechazadas: [{ name, reason }], excedentes: File[], agregadas: number } para que la UI muestre UN solo aviso.
  const addFiles = useCallback(
    async fileList => {
      const { validos, rechazados } = await validarImagenes(fileList, archivo);
      const { aceptados, excedentes } = recortarAlLimite(validos, totalRef.current);
      const items = aceptados.map(file => ({ uuid: uuidv4(), file, estado: 'procesando', error: null, media: null }));
      if (items.length && mounted.current) {
        totalRef.current += items.length;
        setState(prev => ({ ...prev, pending: [...prev.pending, ...items] }));
      }
      const rechazadasProceso = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const { file, rechazada } = await optimizarImagen(item.file, compresor);
        if (!mounted.current) break;
        if (rechazada) {
          rechazadasProceso.push(rechazada);
          setState(prev => ({ ...prev, pending: prev.pending.filter(p => p.uuid !== item.uuid) }));
        } else {
          setState(prev => ({
            ...prev,
            pending: prev.pending.map(p => (p.uuid === item.uuid ? { ...p, file, estado: 'queued' } : p)),
          }));
        }
      }
      return { rechazadas: [...rechazados, ...rechazadasProceso], excedentes, agregadas: items.length - rechazadasProceso.length };
    },
    [archivo, compresor]
  );

  // Quita una imagen pendiente (la descarta el usuario, incluso una que falló).
  const remove = useCallback(uuid => setState(prev => ({ ...prev, pending: prev.pending.filter(p => p.uuid !== uuid) })), []);

  /**
   * Estado de subida de una pendiente: 'procesando' | 'queued' | 'uploading' | 'done' | 'error'.
   * payload: 'done' -> item guardado del backend; 'error' -> mensaje en español. Misma firma que `onImagenEstado` de guardarConHijos.
   */
  const marcar = useCallback((uuid, estado, payload) => {
    if (!mounted.current) return;
    setState(prev => ({
      ...prev,
      pending: prev.pending.map(p =>
        p.uuid === uuid
          ? { ...p, estado, error: estado === 'error' ? payload || null : null, media: estado === 'done' ? payload || null : p.media }
          : p
      ),
    }));
  }, []);

  // Una pendiente 'done' ya está en el backend: pasa a `saved` (la UI llama esto tras mostrar brevemente la marca de éxito).
  const confirmarSubida = useCallback(uuid => {
    setState(prev => {
      const item = prev.pending.find(p => p.uuid === uuid);
      if (!item || item.estado !== 'done') return prev;
      const guardada = item.media && item.media.uuid ? item.media : { ...(item.media || {}), uuid: item.uuid };
      return { ...prev, pending: prev.pending.filter(p => p.uuid !== uuid), saved: [...prev.saved, guardada] };
    });
  }, []);

  // Marca una imagen guardada para eliminarla al guardar (sin API).
  const marcarParaEliminar = useCallback(uuid => {
    setState(prev => ({ ...prev, saved: conMarcaEliminacion(prev.saved, uuid) }));
  }, []);

  // Deshace la marca (la imagen vuelve a verse normal).
  const deshacerEliminar = useCallback(uuid => setState(prev => ({ ...prev, saved: conMarcaEliminacion(prev.saved, uuid, null) })), []);

  /**
   * Progreso de la eliminación al guardar: 'deleting' | 'done' (sale de la lista) | 'error' (sigue marcada; payload = mensaje).
   * Misma firma que `onEliminacionEstado` de guardarConHijos.
   */
  const marcarEliminacion = useCallback((uuid, estado, payload) => {
    if (!mounted.current) return;
    setState(prev => ({ ...prev, saved: conProgresoEliminacion(prev.saved, uuid, estado, payload) }));
  }, []);

  const paraEliminar = uuidsConMarca(state.saved);
  const total = state.saved.length - paraEliminar.length + state.pending.length;
  totalRef.current = total;

  return {
    data: state.pending,
    // Lo que debe subirse al guardar: nuevas y las que fallaron antes (las 'uploading'/'done' no).
    porSubir: state.pending.filter(p => p.estado === 'queued' || p.estado === 'error'),
    // Hay imágenes optimizándose: aún no se pueden subir.
    procesando: state.pending.some(p => p.estado === 'procesando'),
    saved: state.saved,
    // uuids de imágenes guardadas marcadas para eliminar (incluye las que fallaron y se reintentan).
    paraEliminar,
    total,
    // Con 6 o más no se puede agregar; registros que ya traen más se conservan tal cual.
    limiteAlcanzado: total >= MAX_IMAGENES,
    addFiles,
    remove,
    marcar,
    confirmarSubida,
    marcarParaEliminar,
    deshacerEliminar,
    marcarEliminacion,
  };
};

export default useImagenesEditor;
